import { useTranslation } from 'react-i18next';

import { Page } from 'components/Page';

import { config } from 'utils/config/env';

import styles from 'styles/login.module.css';

function PrivacyPolicy() {
  const { t } = useTranslation('privacy-policy');
  const brandName = config.VITE_APP_NAME;

  return (
    <Page
      title="Privacy Policy"
      className="h-full"
      description="Privacy Policy"
    >
      <div className={styles.privacyPolicy}>
        <h2>{t('heading', { brand: brandName })}</h2>

        <div>
          <p>{t('description.intro', { brand: brandName })}</p>
          <p>{t('description.personalInformation', { brand: brandName })}</p>
          <p>{t('description.thirdPartyLinks')}</p>
          <p>{t('description.effectiveDate')}</p>
          <p>{t('description.lastUpdated')}</p>
        </div>

        <h3>{t('informationWeCollect.title')}</h3>
        <p>{t('informationWeCollect.intro')}</p>
        <p>{t('informationWeCollect.voluntarilyProvided')}</p>
        <p>{t('informationWeCollect.automaticallyCollected')}</p>

        <h4>{t('logData.title')}</h4>
        <p>{t('logData.content')}</p>
        <p>{t('logData.errorData')}</p>
        <p>{t('logData.identification')}</p>

        <h4>{t('deviceData.title')}</h4>
        <p>{t('deviceData.intro')}</p>
        <ul>
          {(t('deviceData.items', { returnObjects: true }) as string[]).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            )
          )}
        </ul>
        <p>{t('deviceData.settings')}</p>

        <h4>{t('personalInformation.title')}</h4>
        <p>{t('personalInformation.intro')}</p>
        <ul>
          {(
            t('personalInformation.items', { returnObjects: true }) as string[]
          ).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>

        <h4>{t('transactionData.title')}</h4>
        <p>{t('transactionData.content')}</p>

        <h4>{t('legitimateReasons.title')}</h4>
        <p>{t('legitimateReasons.content')}</p>

        <h4>{t('collectionAndUse.title')}</h4>
        <p>{t('collectionAndUse.intro')}</p>
        <ul>
          {(
            t('collectionAndUse.activities', {
              returnObjects: true,
            }) as string[]
          ).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('collectionAndUse.purposes')}</p>
        <ul>
          {(
            t('collectionAndUse.purposeItems', {
              returnObjects: true,
            }) as string[]
          ).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('collectionAndUse.combination')}</p>

        <h4>{t('security.title')}</h4>
        <p>{t('security.protection')}</p>
        <p>{t('security.limitations')}</p>
        <p>{t('security.responsibility')}</p>

        <h4>{t('retention.title')}</h4>
        <p>{t('retention.duration')}</p>
        <p>{t('retention.legalObligations')}</p>

        <h3>{t('childrensPrivacy.title')}</h3>
        <p>{t('childrensPrivacy.content')}</p>

        <h3>{t('disclosure.title')}</h3>
        <p>{t('disclosure.intro')}</p>
        <ul>
          {(t('disclosure.parties', { returnObjects: true }) as string[]).map(
            (item: string, index: number) => (
              <li key={index}>{item}</li>
            )
          )}
        </ul>
        <p>{t('disclosure.currentThirdParties')}</p>
        <ul>
          {(
            t('disclosure.thirdPartyList', { returnObjects: true }) as string[]
          ).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>

        <h3>{t('yourRights.title')}</h3>
        <p>
          <strong>{t('yourRights.choice.title')}</strong>{' '}
          {t('yourRights.choice.content')}
        </p>
        <p>
          <strong>{t('yourRights.thirdParty.title')}</strong>{' '}
          {t('yourRights.thirdParty.content')}
        </p>
        <p>
          <strong>{t('yourRights.marketing.title')}</strong>{' '}
          {t('yourRights.marketing.content')}
        </p>
        <p>
          <strong>{t('yourRights.access.title')}</strong>{' '}
          {t('yourRights.access.content')}
        </p>
        <p>
          <strong>{t('yourRights.correction.title')}</strong>{' '}
          {t('yourRights.correction.content')}
        </p>
        <p>
          <strong>{t('yourRights.nonDiscrimination.title')}</strong>{' '}
          {t('yourRights.nonDiscrimination.content')}
        </p>
        <p>
          <strong>{t('yourRights.notification.title')}</strong>{' '}
          {t('yourRights.notification.content')}
        </p>
        <p>
          <strong>{t('yourRights.complaints.title')}</strong>{' '}
          {t('yourRights.complaints.content')}
        </p>
        <p>
          <strong>{t('yourRights.unsubscribe.title')}</strong>{' '}
          {t('yourRights.unsubscribe.content')}
        </p>

        <h3>{t('cookies.title')}</h3>
        <p>{t('cookies.content')}</p>
        <p>{t('cookies.policy')}</p>

        <h3>{t('businessTransfers.title')}</h3>
        <p>{t('businessTransfers.content')}</p>

        <h3>{t('limits.title')}</h3>
        <p>{t('limits.content')}</p>

        <h3>{t('changes.title')}</h3>
        <p>{t('changes.discretion')}</p>
        <p>{t('changes.significant')}</p>
        <p>{t('changes.permission')}</p>

        <h3>{t('gdpr.title')}</h3>
        <h4>{t('gdpr.dataController.title')}</h4>
        <p>{t('gdpr.dataController.content', { brand: brandName })}</p>

        <h4>{t('gdpr.legalBases.title')}</h4>
        <p>{t('gdpr.legalBases.intro')}</p>
        <p>{t('gdpr.legalBases.consent')}</p>
        <h5>{t('gdpr.legalBases.consentTitle')}</h5>
        <p>{t('gdpr.legalBases.consentContent')}</p>
        <h5>{t('gdpr.legalBases.contractTitle')}</h5>
        <p>{t('gdpr.legalBases.contractContent')}</p>
        <h5>{t('gdpr.legalBases.legitimateTitle')}</h5>
        <p>{t('gdpr.legalBases.legitimateContent')}</p>
        <h5>{t('gdpr.legalBases.complianceTitle')}</h5>
        <p>{t('gdpr.legalBases.complianceContent')}</p>

        <h4>{t('gdpr.internationalTransfers.title')}</h4>
        <p>{t('gdpr.internationalTransfers.content')}</p>

        <h4>{t('gdpr.yourRights.title')}</h4>
        <p>
          <strong>{t('gdpr.yourRights.restrict.title')}</strong>{' '}
          {t('gdpr.yourRights.restrict.content')}
        </p>
        <p>
          <strong>{t('gdpr.yourRights.object.title')}</strong>{' '}
          {t('gdpr.yourRights.object.content')}
        </p>
        <p>
          <strong>{t('gdpr.yourRights.portability.title')}</strong>{' '}
          {t('gdpr.yourRights.portability.content')}
        </p>
        <p>
          <strong>{t('gdpr.yourRights.deletion.title')}</strong>{' '}
          {t('gdpr.yourRights.deletion.content')}
        </p>

        <h3>{t('california.title')}</h3>
        <p>{t('california.intro')}</p>
        <p>{t('california.request')}</p>
        <p>{t('california.coverage')}</p>

        <h4>{t('california.doNotTrack.title')}</h4>
        <p>{t('california.doNotTrack.content')}</p>
        <p>{t('california.doNotTrack.adherence')}</p>

        <h4>{t('california.cookies.title')}</h4>
        <p>{t('california.cookies.content')}</p>

        <h4>{t('california.financialIncentives.title')}</h4>
        <p>{t('california.financialIncentives.content')}</p>
        <p>{t('california.financialIncentives.terms')}</p>

        <h4>{t('california.notice.title')}</h4>
        <p>{t('california.notice.intro')}</p>
        <ul>
          {(
            t('california.notice.categories', {
              returnObjects: true,
            }) as string[]
          ).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('california.notice.purposes')}</p>

        <h4>{t('california.rights.title')}</h4>
        <p>{t('california.rights.intro')}</p>
        <ul>
          {(
            t('california.rights.items', { returnObjects: true }) as string[]
          ).map((item: string, index: number) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
        <p>{t('california.rights.exercise')}</p>

        <h4>{t('california.shineTheLight.title')}</h4>
        <p>{t('california.shineTheLight.content')}</p>
        <p>{t('california.shineTheLight.request')}</p>

        <h3>{t('contact.title')}</h3>
        <p>{t('contact.intro')}</p>
        <p>{t('contact.details', { brand: brandName })}</p>
      </div>
    </Page>
  );
}

export { PrivacyPolicy };
